import React, { useCallback, useEffect, useRef, useState } from 'react';

interface MediaDropZoneProps {
  onDragEnter: React.DragEventHandler<HTMLDivElement>;
  onDragOver: React.DragEventHandler<HTMLDivElement>;
  onDragLeave: React.DragEventHandler<HTMLDivElement>;
  onDrop: React.DragEventHandler<HTMLDivElement>;
}

interface MediaDropState {
  isDraggingFiles: boolean;
  dropZoneProps: MediaDropZoneProps;
}

export const hasDraggedFiles = (types: readonly string[]): boolean => (
  Array.from(types).includes('Files')
);

const isFileDrag = (event: React.DragEvent<HTMLDivElement>): boolean => (
  hasDraggedFiles(event.dataTransfer.types)
);

// eslint-disable-next-line max-lines-per-function, max-statements
export const useMediaDrop = (
  addFiles: (files: File[]) => void,
  disabled: boolean,
): MediaDropState => {
  const [isDraggingFiles, setIsDraggingFiles] = useState(false);
  const dragDepth = useRef(0);

  const resetDrag = useCallback(() => {
    dragDepth.current = 0;
    setIsDraggingFiles(false);
  }, []);

  useEffect(() => {
    if (!isDraggingFiles) return undefined;

    window.addEventListener('dragend', resetDrag);
    window.addEventListener('drop', resetDrag);

    return () => {
      window.removeEventListener('dragend', resetDrag);
      window.removeEventListener('drop', resetDrag);
    };
  }, [isDraggingFiles, resetDrag]);

  const onDragEnter = useCallback<React.DragEventHandler<HTMLDivElement>>((event) => {
    if (!isFileDrag(event)) return;

    event.preventDefault();
    event.stopPropagation();
    if (disabled) return resetDrag();

    dragDepth.current += 1;
    setIsDraggingFiles(true);
  }, [disabled, resetDrag]);

  const onDragOver = useCallback<React.DragEventHandler<HTMLDivElement>>((event) => {
    if (!isFileDrag(event)) return;

    event.preventDefault();
    event.stopPropagation();
    event.dataTransfer.dropEffect = disabled ? 'none' : 'copy';
    if (disabled && isDraggingFiles) resetDrag();
  }, [disabled, isDraggingFiles, resetDrag]);

  const onDragLeave = useCallback<React.DragEventHandler<HTMLDivElement>>((event) => {
    if (!isFileDrag(event)) return;

    event.preventDefault();
    event.stopPropagation();
    dragDepth.current = Math.max(0, dragDepth.current - 1);
    if (dragDepth.current === 0) setIsDraggingFiles(false);
  }, []);

  const onDrop = useCallback<React.DragEventHandler<HTMLDivElement>>((event) => {
    if (!isFileDrag(event)) return;

    event.preventDefault();
    event.stopPropagation();
    resetDrag();
    if (disabled) return;

    const files = Array.from(event.dataTransfer.files);

    if (files.length > 0) addFiles(files);
  }, [addFiles, disabled, resetDrag]);

  return {
    isDraggingFiles: isDraggingFiles && !disabled,
    dropZoneProps: { onDragEnter, onDragOver, onDragLeave, onDrop },
  };
};
